import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  ArrowRight,
  ArrowLeft,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { toast } from "sonner";

// =========================================================
// PLAYGROUND CONFIG
// =========================================================

const SCOOPS = [
  {
    key: "Small",
    price: 80,
    cal: 200,
  },
  {
    key: "Medium",
    price: 140,
    cal: 300,
  },
  {
    key: "Large",
    price: 210,
    cal: 420,
  },
];

const CONES = [
  {
    name: "Cone",
    price: 10,
  },
  {
    name: "No Cone",
    price: 0,
  },
];

const ADDONS = [
  {
    name: "Oreo (Add-on)",
    price: 10,
    cal: 40,
  },
  {
    name: "Choco Chip (Add-on)",
    price: 10,
    cal: 50,
  },
  {
    name: "Coffee (Add-on)",
    price: 10,
    cal: 30,
  },
  {
    name: "Choco Syrup",
    price: 10,
    cal: 40,
  },
  {
    name: "Almonds",
    price: 15,
    cal: 60,
  },
  {
    name: "Roasted Peanut",
    price: 15,
    cal: 70,
  },
  {
    name: "Cashew",
    price: 15,
    cal: 60,
  },
  {
    name: "Whip Cream",
    price: 20,
    cal: 70,
  },
  {
    name: "Biscoff Biscuit",
    price: 20,
    cal: 70,
  },
  {
    name: "Nutella (Add-on)",
    price: 30,
    cal: 100,
  },
  {
    name: "Extra Cheese",
    price: 20,
    cal: 80,
  },
  {
    name: "Brownie Bites",
    price: 40,
    cal: 160,
  },
  {
    name: "Waffle Cone Chips",
    price: 40,
    cal: 130,
  },
  {
    name: "Banana Slices",
    price: 40,
    cal: 80,
  },
  {
    name: "Extra Ice Cream Scoop",
    price: 50,
    cal: 120,
  },
  {
    name: "Hot Chocolate",
    price: 50,
    cal: 90,
  },
  {
    name: "Brownie (Add-on)",
    price: 50,
    cal: 200,
  },
];

const STEP_NAMES = [
  "Scoop",
  "Cone",
  "Flavours",
  "Add-ons",
];

// =========================================================
// PLAYGROUND
// =========================================================

export default function Playground() {
  const nav = useNavigate();

  const { add } = useCart();

  const [step, setStep] = useState(1);

  const [scoop, setScoop] = useState(SCOOPS[0]);

  const [cone, setCone] = useState("Cone");

  const [flavours, setFlavours] = useState([]);

  const [selectedAddons, setSelectedAddons] = useState([]);

  const [allFlavours, setAllFlavours] = useState([]);

  const [menuItems, setMenuItems] = useState([]);

  const [loading, setLoading] = useState(true);

  // =========================================================
  // LOAD REAL DATABASE MENU
  // =========================================================

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        setLoading(true);

        const [
          flavourResponse,
          menuResponse,
        ] = await Promise.all([
          api.get("/flavours"),
          api.get("/menu-items"),
        ]);

        if (!mounted) return;

        const flavourData =
          Array.isArray(flavourResponse.data)
            ? flavourResponse.data
            : Array.isArray(
                flavourResponse.data?.flavours
              )
            ? flavourResponse.data.flavours
            : [];

        const menuData =
          Array.isArray(menuResponse.data)
            ? menuResponse.data
            : Array.isArray(
                menuResponse.data?.menu_items
              )
            ? menuResponse.data.menu_items
            : [];

        setAllFlavours(flavourData);
        setMenuItems(menuData);
      } catch (error) {
        console.error(
          "Failed to load Playground menu:",
          error
        );

        toast.error(
          "Unable to load the latest menu. Please refresh."
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  // =========================================================
  // REAL FLAVOURS
  // =========================================================

  const flavourList = useMemo(() => {
    return allFlavours
      .map((flavour) => {
        if (typeof flavour === "string") {
          return {
            name: flavour,
            price: 0,
            image: "",
          };
        }

        return {
          name: flavour.name,
          price: Number(flavour.price || 0),
          image: flavour.image || "",
        };
      })
      .filter((flavour) => flavour.name);
  }, [allFlavours]);

  // =========================================================
  // OPTIONAL MENU DATA
  // =========================================================

  const menuCategories = useMemo(() => {
    const categories = new Set();

    menuItems.forEach((item) => {
      if (item.category) {
        categories.add(item.category);
      }
    });

    return Array.from(categories);
  }, [menuItems]);

  // =========================================================
  // TOTAL PRICE
  // =========================================================

  const flavourPrice = useMemo(() => {
    if (!flavours.length) return 0;

    return flavours.reduce((total, flavourName) => {
      const flavour = flavourList.find(
        (item) => item.name === flavourName
      );

      return total + Number(flavour?.price || 0);
    }, 0);
  }, [flavours, flavourList]);

  const addonPrice = useMemo(() => {
    return selectedAddons.reduce(
      (total, addonName) => {
        const addon = ADDONS.find(
          (item) => item.name === addonName
        );

        return total + Number(addon?.price || 0);
      },
      0
    );
  }, [selectedAddons]);

  /*
   * Scoop price is the base price.
   * Flavour price is included only if the database
   * has a non-zero flavour price.
   */
  const totalPrice =
    Number(scoop.price) +
    Number(cone === "Cone" ? 10 : 0) +
    flavourPrice +
    addonPrice;

  // =========================================================
  // TOTAL CALORIES
  // =========================================================

  const flavourCalories = flavours.length * 100;

  const addonCalories = selectedAddons.reduce(
    (total, addonName) => {
      const addon = ADDONS.find(
        (item) => item.name === addonName
      );

      return total + Number(addon?.cal || 0);
    },
    0
  );

  const totalCalories =
    Number(scoop.cal || 0) +
    flavourCalories +
    addonCalories;

  // =========================================================
  // SELECT FLAVOUR
  // =========================================================

  function toggleFlavour(name) {
    setFlavours((previous) => {
      if (previous.includes(name)) {
        return previous.filter(
          (item) => item !== name
        );
      }

      if (previous.length >= 5) {
        toast.error("Maximum 5 flavours allowed.");
        return previous;
      }

      return [...previous, name];
    });
  }

  // =========================================================
  // SELECT ADDON
  // =========================================================

  function toggleAddon(name) {
    setSelectedAddons((previous) => {
      if (previous.includes(name)) {
        return previous.filter(
          (item) => item !== name
        );
      }

      return [...previous, name];
    });
  }

  // =========================================================
  // NEXT
  // =========================================================

  function nextStep() {
    if (
      step === 3 &&
      flavours.length === 0
    ) {
      toast.error(
        "Please select at least one flavour."
      );
      return;
    }

    if (step < STEP_NAMES.length) {
      setStep(
        (current) => current + 1
      );
    }
  }

  // =========================================================
  // BACK
  // =========================================================

  function previousStep() {
    if (step > 1) {
      setStep(
        (current) => current - 1
      );
    }
  }

  // =========================================================
  // ADD TO CART
  // =========================================================

  function handleAddToCart() {
    if (flavours.length === 0) {
      toast.error(
        "Please select at least one flavour."
      );

      setStep(3);

      return;
    }

    const item = {
      kind: "custom",

      name: `Custom Creation (${flavours.length} flavours)`,

      price: Number(totalPrice),

      quantity: 1,

      options: {
        size: scoop.key,

        scoop: scoop.key,

        scoop_price: Number(scoop.price),

        cone,

        flavours: [...flavours],

        addons: [...selectedAddons],

        calories: totalCalories,
      },
    };

    // IMPORTANT:
    // useCart exposes addItem()
    // NOT addToCart()
    add(item);

    toast.success(
      "Custom creation added to cart!"
    );

    // Reset playground
    setStep(1);

    setScoop(SCOOPS[0]);

    setCone("Cone");

    setFlavours([]);

    setSelectedAddons([]);

    // Go to cart
    nav("/cart");
  }

  // =========================================================
  // STEP INDICATOR
  // =========================================================

  function StepIndicator() {
    return (
      <div className="flex items-center justify-center gap-2 mb-10 flex-wrap">
        {STEP_NAMES.map(
          (name, index) => {
            const stepNumber =
              index + 1;

            const active =
              stepNumber === step;

            const completed =
              stepNumber < step;

            return (
              <React.Fragment
                key={name}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (
                      stepNumber <= step
                    ) {
                      setStep(
                        stepNumber
                      );
                    }
                  }}
                  className={[
                    "flex items-center gap-2",
                    "px-4 py-2 rounded-full",
                    "transition",
                    active
                      ? "bg-[#B8E14A] text-black"
                      : completed
                      ? "bg-white/10 text-[#B8E14A]"
                      : "bg-white/5 text-white/40",
                  ].join(" ")}
                >
                  {completed ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className="text-xs font-bold">
                      {stepNumber}
                    </span>
                  )}

                  <span className="text-xs uppercase tracking-wider">
                    {name}
                  </span>
                </button>

                {index <
                  STEP_NAMES.length -
                    1 && (
                  <div className="w-5 h-px bg-white/10" />
                )}
              </React.Fragment>
            );
          }
        )}
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-12 py-8 md:py-12 relative">
      <div className="mb-8 relative">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 text-[#1F5F5C] border mb-3">
          <Sparkles className="w-4 h-4 text-[#B8E14A]" />

          <span className="text-xs uppercase tracking-widest">
            Signature Experience
          </span>
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl">
          The Ice Cream{" "}
          <span className="italic text-[#B8E14A]">
            Playground
          </span>
        </h1>

        <p className="text-[#E6F4F2] mt-3 max-w-xl">
          Design your dream dessert
          step-by-step.
        </p>
      </div>

      <StepIndicator />

      {loading && (
        <div className="text-center py-8 text-white/60">
          Loading latest menu...
        </div>
      )}

      {!loading && (
        <div className="grid lg:grid-cols-5 gap-8">
          {/* =====================================================
              LEFT
          ===================================================== */}

          <div className="lg:col-span-3">
            <AnimatePresence
              mode="wait"
            >
              <motion.div
                key={step}
                initial={{
                  opacity: 0,
                  x: 20,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                exit={{
                  opacity: 0,
                  x: -20,
                }}
                transition={{
                  duration: 0.3,
                }}
              >
                {/* =================================================
                    STEP 1
                ================================================= */}

                {step === 1 && (
                  <div>
                    <h2 className="font-heading text-3xl mb-6">
                      Choose your scoop
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {SCOOPS.map(
                        (item) => (
                          <button
                            key={
                              item.key
                            }
                            type="button"
                            onClick={() =>
                              setScoop(
                                item
                              )
                            }
                            className={`p-6 rounded-3xl border-2 transition text-left ${
                              scoop.key ===
                              item.key
                                ? "border-[#B8E14A] bg-[#B8E14A]/10"
                                : "border-white/10 bg-white/5"
                            }`}
                          >
                            <div className="text-xl font-semibold">
                              {
                                item.key
                              }
                            </div>

                            <div className="text-white/60 mt-2">
                              ₹
                              {
                                item.price
                              }
                            </div>

                            <div className="text-xs text-white/40 mt-1">
                              {
                                item.cal
                              }{" "}
                              calories
                            </div>
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* =================================================
                    STEP 2
                ================================================= */}

                {step === 2 && (
                  <div>
                    <h2 className="font-heading text-3xl mb-6">
                      Choose your cone
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {CONES.map(
                        (item) => (
                          <button
                            key={
                              item.name
                            }
                            type="button"
                            onClick={() =>
                              setCone(
                                item.name
                              )
                            }
                            className={`p-6 rounded-3xl border-2 transition text-left ${
                              cone ===
                              item.name
                                ? "border-[#B8E14A] bg-[#B8E14A]/10"
                                : "border-white/10 bg-white/5"
                            }`}
                          >
                            <div className="text-xl font-semibold">
                              {
                                item.name
                              }
                            </div>

                            <div className="text-white/60 mt-2">
                              {item.price ===
                              0
                                ? "Free"
                                : `₹${item.price}`}
                            </div>
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* =================================================
                    STEP 3
                ================================================= */}

                {step === 3 && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-heading text-3xl">
                        Pick your flavours
                      </h2>

                      <span className="text-sm text-white/60">
                        {flavours.length}/5
                      </span>
                    </div>

                    {flavourList.length ===
                    0 ? (
                      <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-white/60">
                        No flavours available.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {flavourList.map(
                          (flavour) => {
                            const selected =
                              flavours.includes(
                                flavour.name
                              );

                            return (
                              <button
                                key={
                                  flavour.name
                                }
                                type="button"
                                onClick={() =>
                                  toggleFlavour(
                                    flavour.name
                                  )
                                }
                                className={`p-4 rounded-2xl border transition text-left ${
                                  selected
                                    ? "border-[#B8E14A] bg-[#B8E14A]/10"
                                    : "border-white/10 bg-white/5"
                                }`}
                              >
                                <div className="font-semibold text-sm">
                                  {
                                    flavour.name
                                  }
                                </div>

                                {Number(
                                  flavour.price
                                ) >
                                  0 && (
                                  <div className="text-xs text-white/50 mt-1">
                                    ₹
                                    {
                                      flavour.price
                                    }
                                  </div>
                                )}
                              </button>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* =================================================
                    STEP 4
                ================================================= */}

                {step === 4 && (
                  <div>
                    <h2 className="font-heading text-3xl mb-6">
                      Add your toppings
                    </h2>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {ADDONS.map(
                        (addon) => {
                          const selected =
                            selectedAddons.includes(
                              addon.name
                            );

                          return (
                            <button
                              key={
                                addon.name
                              }
                              type="button"
                              onClick={() =>
                                toggleAddon(
                                  addon.name
                                )
                              }
                              className={`p-4 rounded-2xl border transition text-left ${
                                selected
                                  ? "border-[#B8E14A] bg-[#B8E14A]/10"
                                  : "border-white/10 bg-white/5"
                              }`}
                            >
                              <div className="font-semibold text-sm">
                                {
                                  addon.name
                                }
                              </div>

                              <div className="text-xs text-white/50 mt-1">
                                ₹
                                {
                                  addon.price
                                }
                              </div>
                            </button>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* =====================================================
                NAVIGATION
            ===================================================== */}

            <div className="flex justify-between mt-8 gap-4">
              <button
                type="button"
                onClick={
                  previousStep
                }
                disabled={
                  step === 1
                }
                className="px-6 py-3 rounded-full border border-white/20 disabled:opacity-30"
              >
                <ArrowLeft className="inline w-4 h-4 mr-2" />
                Back
              </button>

              {step < 4 ? (
                <button
                  type="button"
                  onClick={
                    nextStep
                  }
                  className="px-6 py-3 rounded-full bg-[#B8E14A] text-black font-semibold"
                >
                  Continue
                  <ArrowRight className="inline w-4 h-4 ml-2" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={
                    handleAddToCart
                  }
                  className="px-6 py-3 rounded-full bg-[#B8E14A] text-black font-semibold"
                >
                  Add to Cart
                </button>
              )}
            </div>
          </div>

          {/* =====================================================
              PREVIEW
          ===================================================== */}

          <div className="lg:col-span-2">
            <div className="sticky top-6 rounded-3xl border border-white/10 bg-white/5 p-6">
              <div className="text-xs uppercase tracking-widest text-[#B8E14A]">
                Your Creation
              </div>

              <h2 className="font-heading text-3xl mt-2">
                Custom Ice Cream
              </h2>

              <div className="mt-6 space-y-4">
                <SummaryRow
                  label="Scoop"
                  value={`${scoop.key} — ₹${scoop.price}`}
                />

                <SummaryRow
                  label="Cone"
                  value={
                    cone === "Cone"
                      ? "Cone — ₹10"
                      : "No Cone"
                  }
                />

                <SummaryRow
                  label="Flavours"
                  value={
                    flavours.length
                      ? flavours.join(
                          ", "
                        )
                      : "None selected"
                  }
                />

                <SummaryRow
                  label="Add-ons"
                  value={
                    selectedAddons.length
                      ? selectedAddons.join(
                          ", "
                        )
                      : "None"
                  }
                />
              </div>

              <div className="border-t border-white/10 mt-6 pt-6">
                <div className="flex justify-between items-center">
                  <span className="text-white/60">
                    Total
                  </span>

                  <span className="text-3xl font-heading text-[#B8E14A]">
                    ₹
                    {Number(
                      totalPrice
                    ).toFixed(0)}
                  </span>
                </div>

                <div className="text-xs text-white/40 mt-2">
                  Approx.{" "}
                  {totalCalories}{" "}
                  calories
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================
// SUMMARY ROW
// =========================================================

function SummaryRow({
  label,
  value,
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-white/40">
        {label}
      </div>

      <div className="text-sm text-white/80 mt-1">
        {value}
      </div>
    </div>
  );
}